import React, { useMemo } from 'react'

import { CepApprovedIcon, Label, Spinner, VsStar2Icon } from '@octane/spark'
import { ApprovalTerm } from 'api/application'
import ClientApplication from 'api/application/interfaces/ClientApplication'
import { useFeatureFlagContext } from 'contexts/FeatureFlagContextProvider'
import useApprovalTerms from 'hooks/useApprovalTerms'
import useMaxDealerReservePerTerm from 'hooks/useMaxDealerReservePerTerm'
import AssetType from 'state/enums/AssetType'
import LenderName from 'state/enums/LenderName'
import VehicleType from 'state/enums/VehicleType'
import {
    getMaxFinancedAmount,
    getMaxFinancedTerm,
} from 'utils/approvalTerms/getMaxFinancedTerm'
import { getMinAPR } from 'utils/approvalTerms/getMinAPR'
import { formatCurrency } from 'utils/formatCurrency'

import {
    ApprovedTitle,
    AprText,
    BodyText,
    Container,
    LabelWrapper,
    LenderBanner,
    LoaderWrapper,
} from './DecisionResult.styled'
import ReserveCallout from './ResultReserveCallout'
import WorkTheDealButton from './WorkTheDealButton'

const PRIME_RISK_TIERS = ['1', '2']

const hasPrimeRiskTier = (maxFinancedTerm: ApprovalTerm) => {
    return PRIME_RISK_TIERS.includes(maxFinancedTerm.risk_tier)
}

export default function DecisionResultApproved({
    clientApplication,
    vehicleType,
    vehicleCondition,
}: {
    readonly clientApplication: ClientApplication
    readonly vehicleType: string
    readonly vehicleCondition: string
}): JSX.Element {
    const { approvalTerms, loading, error } = useApprovalTerms(
        clientApplication?.uuid,
    )

    const { reservesByTermId, loading: reservesLoading } =
        useMaxDealerReservePerTerm(clientApplication?.uuid)

    const maxReserve = useMemo(
        () =>
            Object.values(reservesByTermId).reduce(
                (max, v) => Math.max(max, v),
                0,
            ),
        [reservesByTermId],
    )

    const maxFinancedTerm = getMaxFinancedTerm(approvalTerms)

    const maxFinancedAmount = maxFinancedTerm
        ? getMaxFinancedAmount(maxFinancedTerm)
        : undefined

    const hasGuaranteedBackend =
        Number(maxFinancedTerm?.guaranteed_backend_amount) > 0

    const isPrime = !!maxFinancedTerm && hasPrimeRiskTier(maxFinancedTerm)
    const featureFlags = useFeatureFlagContext()

    const isPowersportsApplication =
        clientApplication.asset_type === AssetType.POWERSPORTS_VEHICLE

    const isTrailer = vehicleType === VehicleType.TRAILER

    const hasTrailerAdvance =
        isPowersportsApplication &&
        !isTrailer &&
        clientApplication.lender_name !== LenderName.HUNTINGTON_NATIONAL_BANK

    const hasAllInAdvance =
        featureFlags?.singleBucket &&
        isPrime &&
        clientApplication.asset_type === AssetType.POWERSPORTS_VEHICLE &&
        vehicleType !== VehicleType.PWC &&
        vehicleCondition === 'N'

    const noIncomeVerificationRequired =
        clientApplication.requires_income_verification === false

    const hasOemAccessoriesAdvance =
        Number(maxFinancedTerm?.additional_oem_accessories_dollar_amount) > 0

    const renderLabels = () => {
        if (
            !isPrime &&
            !hasGuaranteedBackend &&
            !noIncomeVerificationRequired &&
            !hasAllInAdvance
        ) {
            return null
        }

        return (
            <LabelWrapper>
                {isPrime && (
                    <Label
                        text="Prime"
                        color="success"
                        startIcon={VsStar2Icon}
                    />
                )}
                {noIncomeVerificationRequired && (
                    <Label
                        text="No Income Verification Required"
                        color="success"
                    />
                )}
                {hasGuaranteedBackend && !hasAllInAdvance && (
                    <Label text="Guaranteed Backend" color="success" />
                )}
                {hasAllInAdvance && (
                    <Label text="All-in Advance" color="success" />
                )}
            </LabelWrapper>
        )
    }

    const getAdvanceText = () => {
        if (!hasAllInAdvance) {
            if (hasOemAccessoriesAdvance && hasTrailerAdvance) {
                return 'with Accessories Advance + Trailer Advance'
            }

            if (!hasTrailerAdvance && hasOemAccessoriesAdvance) {
                return 'with Accessories Advance'
            }
        }

        if (hasTrailerAdvance) {
            return 'with Trailer Advance'
        }

        return ''
    }

    const renderApprovalSummary = () => {
        if (loading || (approvalTerms.length > 0 && reservesLoading)) {
            return (
                <LoaderWrapper
                    data-testid="DecisionResultApproved-spinner"
                    style={{ height: '120px' }}
                >
                    <Spinner size={24} />
                </LoaderWrapper>
            )
        }

        if (error && approvalTerms.length === 0) {
            return (
                <BodyText>
                    An error occurred when loading approval terms. Refresh the
                    page to try again or contact support.
                </BodyText>
            )
        }

        if (!maxFinancedAmount) {
            return <BodyText>No approval terms available.</BodyText>
        }

        return (
            <>
                {renderLabels()}
                <ReserveCallout amount={maxReserve} />
                <LenderBanner
                    lenderName={clientApplication.lender_name as LenderName}
                />
                <BodyText>
                    Max Financed{' '}
                    <strong>{formatCurrency(maxFinancedAmount)}</strong>
                    <br />
                    <span>{getAdvanceText()}</span>
                    <AprText>
                        Rates as low as{' '}
                        <strong>{getMinAPR(approvalTerms)}%</strong>
                    </AprText>
                </BodyText>
            </>
        )
    }

    return (
        <Container data-testid="DecisionResultApproved">
            <CepApprovedIcon width={100} height={100} />
            <ApprovedTitle>Approved</ApprovedTitle>

            {renderApprovalSummary()}
            {clientApplication?.id ? (
                <WorkTheDealButton
                    applicationId={clientApplication.id}
                    disabled={clientApplication.has_active_contracts}
                    blockerMessage={
                        clientApplication.redecision_blockers?.[0]?.message
                    }
                />
            ) : null}
        </Container>
    )
}
